// Shareable links for the Three-Point Estimate calculator: the tasks and the two
// trust settings, in `#pert=…` (see src/lib/share.js).

import { defineShare, list, num, obj, oneOf, str } from "../lib/share.js";
import { MAX_TASKS, TOGETHER, UNITS, WIDEN } from "./model.js";

const MAX_VALUE = 1e6;

export const pertShare = defineShare({
  key: "pert",
  version: 1,
  docs: {
    summary: "Add up three-point estimates (optimistic, most likely, pessimistic) by simulation, and read the chance of finishing within a total.",
    fields: {
      unit: "Unit of every number: days, hours, weeks or points.",
      widen: "How far to widen each stated range around its most likely value: as-stated, wider (×1.5) or much-wider (×2).",
      together: "How strongly tasks run late together: independent, some or strong (a shared overrun multiplier).",
      target: "Total to report the chance of finishing within, or null for none.",
      tasks: `Up to ${MAX_TASKS} tasks: label, low (optimistic), likely (most likely) and high (pessimistic), with low ≤ likely ≤ high and low ≥ 0.`,
    },
    caveats: [
      "Each task is a Beta-PERT bounded at its pessimistic value; widen the ranges if they are usually too narrow.",
      "The total is conditional on the task list being complete. Work nobody listed is not in it.",
    ],
  },
  toState: (s) => ({
    unit: s.unit,
    widen: s.widen,
    together: s.together,
    target: s.target,
    tasks: s.tasks.map(({ label, low, likely, high }) => ({ label, low, likely, high })),
  }),
  fromState: (raw) => {
    const tasks = list(
      raw.tasks,
      (t) => {
        const o = obj(t);
        return {
          label: str(o.label, "Task", 120),
          low: num(o.low, NaN, 0, MAX_VALUE),
          likely: num(o.likely, NaN, 0, MAX_VALUE),
          high: num(o.high, NaN, 0, MAX_VALUE),
        };
      },
      [],
      MAX_TASKS,
    );
    if (!tasks.length) throw new Error("tasks");
    return {
      unit: oneOf(raw.unit, UNITS, "days"),
      widen: oneOf(raw.widen, WIDEN.map((w) => w.id), "as-stated"),
      together: oneOf(raw.together, TOGETHER.map((t) => t.id), "independent"),
      target: raw.target == null ? null : num(raw.target, null, 0, MAX_VALUE),
      tasks,
    };
  },
});
